import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Helmet } from 'react-helmet-async';
import {
  Container,
  Grid,
  Typography,
  Button,
  Chip,
  Box,
  Card,
  CardContent,
  Avatar,
  Divider,
  IconButton,
  Skeleton,
  Alert,
  Snackbar,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  ArrowBack,
  LocationOn,
  CalendarToday,
  Euro,
  Share,
  Favorite,
  FavoriteBorder,
  Send,
  Phone,
  Email,
  Business,
  Person,
  AccessTime,
  Group
} from '@mui/icons-material';
import { selectMissionById, toggleFavorite, fetchMissionById, applyToMission } from '../redux/slices/missionsSlice';
import { fetchDashboardData } from '../redux/slices/professionalDashboardSlice';
import organizationService from '../utils/organizationService';
import Breadcrumbs from '../components/Breadcrumbs';
import './MissionDetail.css';

const MissionDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showContactInfo, setShowContactInfo] = useState(false);
  const [orgRating, setOrgRating] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Get mission from Redux store
  const missionFromList = useSelector(state => selectMissionById(state, id));
  const selectedMission = useSelector(state => state.missions.selectedMission);
  const mission = missionFromList || selectedMission;
  const { favorites } = useSelector(state => state.missions);
  
  const isFavorite = favorites.includes(String(id));
  
  useEffect(() => {
    let timer;
    if (!mission) {
      dispatch(fetchMissionById(id));
      timer = setTimeout(() => {
        if (!mission) {
          setError('Mission non trouvée');
        }
        setLoading(false);
      }, 800);
    } else {
      setLoading(false);
    }
    return () => timer && clearTimeout(timer);
  }, [dispatch, id, mission]);

  useEffect(() => {
    const loadOrgStats = async () => {
      if (mission && mission.organizationId) {
        try {
          const res = await organizationService.getOrganizationById(mission.organizationId);
          const rating = res?.data?.stats?.rating?.average || 0;
          setOrgRating(rating);
        } catch (e) {
          setOrgRating(0);
        }
      }
    };
    loadOrgStats();
  }, [mission]);
  
  const handleBack = () => {
    navigate('/missions');
  };
  
  const handleToggleFavorite = () => {
    dispatch(toggleFavorite(String(id)));
  };
  
  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: mission.title,
          text: mission.description,
          url: window.location.href
        });
      } catch (error) {
        console.log('Erreur lors du partage:', error);
      }
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(window.location.href);
      setSnackbar({ open: true, message: 'Lien copié dans le presse-papiers', severity: 'success' });
    }
  };
  
  const handleApply = async () => {
    try {
      const applicationData = {
        motivationLetter: 'Je souhaite participer à cette mission et mettre mon expérience à profit. Disponible aux dates indiquées et motivé pour contribuer à la réussite.'
      };
      await dispatch(applyToMission(id, applicationData));
      await dispatch(fetchDashboardData());
      setSnackbar({ open: true, message: 'Candidature envoyée avec succès', severity: 'success' });
    } catch (e) {
      setSnackbar({ open: true, message: e?.message || 'Erreur lors de l\'envoi de la candidature', severity: 'error' });
    }
  };
  
  const handleContact = () => {
    setShowContactInfo(!showContactInfo);
  };
  
  if (loading) {
    return (
      <div className="mission-detail-page">
        <Container maxWidth="lg">
          <Breadcrumbs />
          <div className="mission-detail-loading">
            <Skeleton variant="rectangular" height={300} className="mission-detail-image-skeleton" />
            <Skeleton variant="text" height={60} />
            <Skeleton variant="text" height={40} width="60%" />
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <Skeleton variant="rectangular" height={200} />
              </Grid>
              <Grid item xs={12} md={4}>
                <Skeleton variant="rectangular" height={300} />
              </Grid>
            </Grid>
          </div>
        </Container>
      </div>
    );
  }
  
  if (error || !mission) {
    return (
      <div className="mission-detail-page">
        <Container maxWidth="lg">
          <Breadcrumbs />
          <Alert severity="error" className="mission-detail-error">
            {error || 'Mission non trouvée'}
          </Alert>
          <Button
            startIcon={<ArrowBack />}
            onClick={handleBack}
            className="back-button"
          >
            Retour aux missions
          </Button>
        </Container>
      </div>
    );
  }
  
  const orgName = (mission.organization && typeof mission.organization === 'object')
    ? mission.organization.name
    : mission.organization || '';
  const locationText = (mission.location && typeof mission.location === 'object' && mission.location.address)
    ? `${mission.location.address.city}, ${mission.location.address.postalCode}`
    : (typeof mission.location === 'string' ? mission.location : 'Localisation non spécifiée');

  const breadcrumbItems = [
    { label: 'Accueil', path: '/' },
    { label: 'Missions', path: '/missions' },
    { label: mission.title, path: `/missions/${id}` }
  ];
  
  return (
    <div className="mission-detail-page">
      <Helmet>
        {(() => {
          const pageTitle = typeof mission.title === 'string' && mission.title.trim() ? mission.title : 'Mission';
          const pageDescription = typeof mission.description === 'string' ? mission.description : '';
          const pageImage = mission.image || '/assets/images/logo.png';
          return (
            <>
              <title>{`${pageTitle} - Mission Sport`}</title>
              <meta name="description" content={pageDescription} />
              <meta property="og:title" content={pageTitle} />
              <meta property="og:description" content={pageDescription} />
              <meta property="og:image" content={pageImage} />
              <meta property="og:url" content={window.location.href} />
              <meta property="og:type" content="article" />
              <meta name="twitter:card" content="summary_large_image" />
              <meta name="twitter:title" content={pageTitle} />
              <meta name="twitter:description" content={pageDescription} />
              <meta name="twitter:image" content={pageImage} />
            </>
          );
        })()}
      </Helmet>
      
      <Container maxWidth="lg">
        <Breadcrumbs customItems={breadcrumbItems} />
        
        {/* Header */}
        <div className="mission-detail-header">
          <Button
            startIcon={<ArrowBack />}
            onClick={handleBack}
            className="back-button"
            size={isMobile ? 'small' : 'medium'}
          >
            Retour
          </Button>
          
          <div className="mission-detail-actions">
            <IconButton
              onClick={handleShare}
              className="action-button"
              aria-label="Partager"
            >
              <Share />
            </IconButton>
            <IconButton
              onClick={handleToggleFavorite}
              className={`action-button ${isFavorite ? 'favorite' : ''}`}
              aria-label={isFavorite ? 'Retirer des favoris' : 'Ajouter aux favoris'}
            >
              {isFavorite ? <Favorite /> : <FavoriteBorder />}
            </IconButton>
          </div>
        </div>
        
        {/* Hero Image */}
        <div className="mission-detail-hero">
          <img
            src={mission.image}
            alt={mission.title}
            className="mission-detail-image"
            loading="eager"
          />
          <div className="mission-detail-overlay">
            <Chip
              label={mission.discipline}
              className="mission-discipline-chip"
              size="small"
            />
          </div>
        </div>
        
        <Grid container spacing={4} className="mission-detail-content">
          {/* Main Content */}
          <Grid item xs={12} md={8}>
            <div className="mission-detail-main">
              <Typography variant="h3" component="h1" className="mission-title">
                {mission.title}
              </Typography>
              
              <div className="mission-meta">
                <div className="mission-meta-item">
                  <LocationOn className="meta-icon" />
                  <Typography variant="body2">{locationText}</Typography>
                </div>
                <div className="mission-meta-item">
                  <CalendarToday className="meta-icon" />
                  <Typography variant="body2">{mission.date}</Typography>
                </div>
                <div className="mission-meta-item">
                  <Euro className="meta-icon" />
                  <Typography variant="body2">{mission.rate}</Typography>
                </div>
                <div className="mission-meta-item">
                  <AccessTime className="meta-icon" />
                  <Typography variant="body2">{mission.duration || '2-4 heures'}</Typography>
                </div>
              </div>
              
              <Divider className="section-divider" />
              
              {/* Description */}
              <section className="mission-section">
                <Typography variant="h5" component="h2" className="section-title">
                  Description de la mission
                </Typography>
                <Typography variant="body1" className="mission-description">
                  {mission.description}
                </Typography>
                <Typography variant="body1" className="mission-description">
                  Cette mission s'adresse à des professionnels passionnés souhaitant partager leur expertise 
                  et contribuer au développement du sport dans notre région. Vous aurez l'opportunité de 
                  travailler avec une équipe dynamique et de faire une réelle différence.
                </Typography>
              </section>
              
              <Divider className="section-divider" />
              
              {/* Requirements */}
              <section className="mission-section">
                <Typography variant="h5" component="h2" className="section-title">
                  Compétences requises
                </Typography>
                <div className="mission-requirements">
                  <Chip label="Expérience en coaching" variant="outlined" className="requirement-chip" />
                  <Chip label="Diplôme BPJEPS" variant="outlined" className="requirement-chip" />
                  <Chip label="Premiers secours" variant="outlined" className="requirement-chip" />
                  <Chip label="Disponibilité week-end" variant="outlined" className="requirement-chip" />
                  <Chip label="Véhicule personnel" variant="outlined" className="requirement-chip" />
                </div>
              </section>
              
              <Divider className="section-divider" />
              
              {/* Additional Info */}
              <section className="mission-section">
                <Typography variant="h5" component="h2" className="section-title">
                  Informations complémentaires
                </Typography>
                <div className="mission-additional-info">
                  <div className="info-item">
                    <Group className="info-icon" />
                    <div>
                      <Typography variant="subtitle2">Nombre de participants</Typography>
                      <Typography variant="body2">15-20 personnes</Typography>
                    </div>
                  </div>
                  <div className="info-item">
                    <Business className="info-icon" />
                    <div>
                      <Typography variant="subtitle2">Type de structure</Typography>
                      <Typography variant="body2">Association sportive</Typography>
                    </div>
                  </div>
                  <div className="info-item">
                    <Person className="info-icon" />
                    <div>
                      <Typography variant="subtitle2">Public cible</Typography>
                      <Typography variant="body2">Adultes débutants</Typography>
                    </div>
                  </div>
                </div>
              </section>
            </div>
          </Grid>
          
          {/* Sidebar */}
          <Grid item xs={12} md={4}>
            <div className="mission-detail-sidebar">
              {/* Organization Card */}
              <Card className="organization-card">
                <CardContent>
                  <div className="organization-header">
                    <Avatar
                      src={mission.organizationLogo}
                      alt={orgName || ''}
                      className="organization-avatar"
                    >
                      {orgName && typeof orgName === 'string' ? orgName.charAt(0) : ''}
                    </Avatar>
                    <div>
                      <Typography variant="h6" className="organization-name">
                        {mission.organization}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Organisation sportive
                      </Typography>
                    </div>
                  </div>
                  
                  <Typography variant="body2" className="organization-description">
                    Association dédiée au développement du sport pour tous, 
                    avec plus de 10 ans d'expérience dans l'organisation d'événements sportifs.
                  </Typography>
                  
                  {showContactInfo && (
                    <div className="contact-info">
                      <div className="contact-item">
                        <Phone className="contact-icon" />
                        <Typography variant="body2">01 23 45 67 89</Typography>
                      </div>
                      <div className="contact-item">
                        <Email className="contact-icon" />
                      <Typography variant="body2">{orgName ? `contact@${orgName.toLowerCase().replace(/\s+/g, '')}.fr` : ''}</Typography>
                    </div>
                    </div>
                  )}
                </CardContent>
              </Card>
              
              {/* Action Buttons */}
              <div className="mission-actions">
                <Button
                  variant="contained"
                  fullWidth
                  size="large"
                  onClick={handleApply}
                  className="apply-button"
                  startIcon={<Send />}
                >
                  Postuler à cette mission
                </Button>
                
                
              </div>
              
              {/* Mission Stats */}
              <Card className="mission-stats-card">
                <CardContent>
                  <Typography variant="h6" className="stats-title">
                    Statistiques
                  </Typography>
                  <div className="stats-grid">
                    <div className="stat-item">
                      <Typography variant="h4" className="stat-number">{typeof mission.applicationCount === 'number' ? mission.applicationCount : 0}</Typography>
                      <Typography variant="body2" color="textSecondary">Candidatures</Typography>
                    </div>
                    <div className="stat-item">
                      <Typography variant="h4" className="stat-number">{typeof mission.daysUntilStart === 'number' ? mission.daysUntilStart : 0}</Typography>
                      <Typography variant="body2" color="textSecondary">Jours restants</Typography>
                    </div>
                    <div className="stat-item">
                      <Typography variant="h4" className="stat-number">{orgRating || 0}</Typography>
                      <Typography variant="body2" color="textSecondary">Note organisation</Typography>
                    </div>
                    <div className="stat-item">
                      <Typography variant="h4" className="stat-number">{mission.successRate ? `${mission.successRate}%` : '0%'}</Typography>
                      <Typography variant="body2" color="textSecondary">Taux de réussite</Typography>
                    </div>
                  </div>
              </CardContent>
              </Card>
              {/* Snackbar notifications */}
              <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
              >
                <Alert
                  onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                  severity={snackbar.severity}
                  sx={{ width: '100%' }}
                >
                  {snackbar.message}
                </Alert>
              </Snackbar>
            </div>
          </Grid>
        </Grid>
      </Container>
    </div>
  );
};

export default MissionDetail;